import ItemGroupPackage from "@shared/components/application/data-display/item-group/ItemGroupPackage/ItemGroupPackage.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import type { ItemGroupSharedProps } from "./ItemGroup.props";
import ItemGroupAssignedSeats from "./ItemGroupAssignedSeats/ItemGroupAssignedSeats.web";
import ItemGroupDeliveryMethod from "./ItemGroupDeliveryMethod/ItemGroupDeliveryMethod.web";
import ItemGroupDisplay from "./ItemGroupDisplay/ItemGroupDisplay.web";
import ItemGroupDonation from "./ItemGroupDonation/ItemGroupDonation.web";
import ItemGroupEvent from "./ItemGroupEvent/ItemGroupEvent.web";
import ItemGroupGiftCard from "./ItemGroupGiftCard/ItemGroupGiftCard.web";
import ItemGroupMembership from "./ItemGroupMembership/ItemGroupMembership.web";
import ItemGroupProduct from "./ItemGroupProduct/ItemGroupProduct.web";
import ItemGroupSkeleton from "./ItemGroupSkeleton/ItemGroupSkeleton.web";

export type ItemGroupProps = ItemGroupSharedProps & {
  // Add any web specific props here.
};

export default function ItemGroup({
  itemGroup,
  children,
  isParentLoading,
  header,
  handleRemoveAssignedSeat,
  handleChangeQuantity,
  handleChangeShippingType,
  updateItemTracking,
  currency,
  isBasketUpdating,
  packageChildItems,
  packageChildQuantity,
  adjustmentLabels,
  showNewItemPrice,
  eventService,
  membershipService,
  productService,
  allowQuantityChange = true,
}: ItemGroupProps): JSX.Element {
  const {
    quantity,
    ig_subtotal: total,
    subtotal: originalTotal,
    price: perItemPrice,
  } = itemGroup;

  return (
    <Box
      p="md"
      border="1px solid"
      borderColor="border"
      borderRadius="md"
      data-testid="item-group-container"
    >
      <Flex direction="column">
        {header}
        {itemGroup.event && itemGroup.venue_id && !itemGroup.charity_id && (
          <ItemGroup.Event
            id={itemGroup.id}
            eventId={itemGroup.event}
            allowQuantityChange={allowQuantityChange}
            quantity={quantity}
            seatPermissions={itemGroup.permissions ?? []}
            locationPermissions={itemGroup.location_permissions ?? []}
            ticketTypeId={itemGroup.type}
            isParentLoading={isParentLoading}
            handleRemoveAssignedSeat={handleRemoveAssignedSeat}
            handleChangeQuantity={handleChangeQuantity}
            handleChangeShippingType={handleChangeShippingType}
            updateItemTracking={updateItemTracking}
            timezone={itemGroup.event_timezone}
            startsOn={itemGroup.event}
            currency={currency}
            perItemPrice={perItemPrice}
            originalTotal={originalTotal}
            isBasketUpdating={isBasketUpdating}
            packageChildItems={packageChildItems}
            packageChildQuantity={packageChildQuantity}
            total={total}
            adjustmentLabels={adjustmentLabels}
            showNewItemPrice={showNewItemPrice}
            eventService={eventService}
            membershipService={membershipService}
            productService={productService}
          />
        )}
        {itemGroup.product && itemGroup.venue_id && (
          <ItemGroup.Product
            id={itemGroup.id}
            productId={itemGroup.product}
            allowQuantityChange={allowQuantityChange}
            productAttribute={itemGroup.product_attribute}
            quantity={quantity}
            isParentLoading={isParentLoading}
            currency={currency}
            perItemPrice={perItemPrice}
            originalTotal={originalTotal}
            handleChangeQuantity={handleChangeQuantity}
            updateItemTracking={updateItemTracking}
            isBasketUpdating={isBasketUpdating}
            packageChildItems={packageChildItems}
            packageChildQuantity={packageChildQuantity}
            total={total}
            adjustmentLabels={adjustmentLabels}
            showNewItemPrice={showNewItemPrice}
            eventService={eventService}
            membershipService={membershipService}
            productService={productService}
          />
        )}
        {itemGroup.membership_group && itemGroup.venue_id && (
          <>
            <ItemGroup.Membership
              id={itemGroup.id}
              isRenewalItemGroup={Boolean(itemGroup.renewal_member)}
              renewalMemberExpiryDate={
                itemGroup?.renewal_member_info?.expiry_date
              }
              membershipLevelId={itemGroup.membership_level}
              membershipGroupId={itemGroup.membership_group}
              quantity={quantity}
              allowQuantityChange={allowQuantityChange}
              isParentLoading={isParentLoading}
              currency={currency}
              perItemPrice={perItemPrice}
              originalTotal={originalTotal}
              handleChangeQuantity={handleChangeQuantity}
              updateItemTracking={updateItemTracking}
              isBasketUpdating={isBasketUpdating}
              seatPermissions={itemGroup.permissions ?? []}
              locationPermissions={itemGroup.location_permissions ?? []}
              packageChildItems={packageChildItems}
              packageChildQuantity={packageChildQuantity}
              total={total}
              adjustmentLabels={adjustmentLabels}
              showNewItemPrice={showNewItemPrice}
              eventService={eventService}
              membershipService={membershipService}
              productService={productService}
            />
          </>
        )}

        {itemGroup.charity_id && (
          <ItemGroup.Donation
            id={itemGroup.id}
            eventId={itemGroup.event}
            quantity={quantity}
            ticketTypeId={itemGroup.type}
            isParentLoading={isParentLoading}
            updateItemTracking={updateItemTracking}
            currency={currency}
            originalTotal={originalTotal}
            isBasketUpdating={isBasketUpdating}
            total={total}
            eventService={eventService}
            membershipService={membershipService}
            productService={productService}
            isDonation
          />
        )}

        {itemGroup.gift_card && (
          <ItemGroup.GiftCard
            id={itemGroup.id}
            quantity={quantity}
            currency={currency}
            originalTotal={originalTotal}
            isBasketUpdating={isBasketUpdating}
            total={total}
            eventService={eventService}
            membershipService={membershipService}
            productService={productService}
            // Gift card name is the itemGroup description when type is gift card
            name={itemGroup.description || ""}
          />
        )}
        {/* Children used in Split holds to display input fields */}
        {!isParentLoading && children}
      </Flex>
    </Box>
  );
}

ItemGroup.Event = ItemGroupEvent;

ItemGroup.Donation = ItemGroupDonation;

ItemGroup.Product = ItemGroupProduct;

ItemGroup.Membership = ItemGroupMembership;

ItemGroup.GiftCard = ItemGroupGiftCard;

ItemGroup.Display = ItemGroupDisplay;

ItemGroup.AssignedSeats = ItemGroupAssignedSeats;

ItemGroup.Package = ItemGroupPackage;

ItemGroup.DeliveryMethod = ItemGroupDeliveryMethod;

// Skeleton with placeholder values for the ItemGroup component to match styling
ItemGroup.Skeleton = ItemGroupSkeleton;
